import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnInit
} from "@angular/core";
import {BehaviorSubject} from "rxjs";
import {DestroyStreamService} from "../../../services";
import {takeUntil} from "rxjs/operators";
import {IntResizeObserverService} from "../../../modules";

export type TGridContainerBreakpoints = {
  width: number;
  columnsFormula: number | string;
  gap?: string;
}

@Component({
  selector: 'int-grid-container, [int-grid-container], [intGridContainer]',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./int-grid-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService,
    IntResizeObserverService
  ]
})
export class IntGridContainerComponent implements OnInit {

  @Input() columnsAmount: number | string = 1;
  @Input() gap = '1rem';
  @Input() defaultColumnSize = '1fr';
  @Input() breakpoints: Array<TGridContainerBreakpoints> = [];

  width$ = new BehaviorSubject(0);

  currentColumnsAmount: null | number | string = null;
  currentGap: null | string = null;

  constructor(
    private readonly destroy$: DestroyStreamService,
    private readonly resizeObserverService: IntResizeObserverService
  ) {
    if(this.resizeObserverService) {
      this.createResizeObserver();
    }
  }

  @HostBinding('style.--columns-amount')
  get _columns(): string {
    return this.generateColumnsValue(this.currentColumnsAmount || this.columnsAmount);
  }

  @HostBinding('style.--grid-gap')
  get _gap(): string {
    return this.currentGap || this.gap;
  }

  ngOnInit() {

    const sorting = this.breakpoints.sort((prev, next) => {
      return prev.width - next.width > 0 ? 1 : prev.width - next.width < 0 ? -1 : 0;
    }).map((point, i, list) => {
      if(i === 0) {
        return {
          ...point,
          min: 0,
          max: point.width,
        }
      }else if(i === list.length - 1) {
        return {
          ...point,
          min: list[i - 1].width + 1,
          max: point.width,
        }
      }else{
        return {
          ...point,
          min: list[i - 1].width + 1,
          max: point.width,
        }
      }
    });

    this.width$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const _data = sorting.find(({max, min, columnsFormula}) => {
          return (value > min) && (value <= max)
        });

        this.currentColumnsAmount = _data?.columnsFormula || null;
        this.currentGap = _data?.gap || null;
      })
  }


  createResizeObserver() {
    this.resizeObserverService.observable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((entries) => {
        const width = entries[0].borderBoxSize[0].inlineSize;

        this.width$.next(width);
      })
  }

  generateColumnsValue(formula: number | string) {
    if(typeof formula === 'number') {
      return Array(formula).fill(0).map(() => this.defaultColumnSize).join(' ');
    }else{
      return formula;
    }
  }
}
